import { useEffect, useState } from 'react';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client/react';
import * as R from 'ramda';

import { GET_ASSET } from '@atom/graph/asset';
import {
  GET_ASSET_REQUEST,
  UPDATE_ASSET_REQUEST,
} from '@atom/graph/assetRequest';
import { Modal, Progress, Snackbar } from '@atom/mui';
import { AssetDetail } from '@atom/types/asset';
import {
  AssetRequest,
  AssetRequestStatus,
  AssetRequestType,
} from '@atom/types/assetRequest';
import { isAssetRefreshOnRequestApprovalEnabled } from '@atom/utilities/tenantFeatureUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';

import {
  ADOT_UPDATE_FORBIDDEN_MESSAGE,
  ADOT_UPDATE_SNACKBAR_MESSAGES,
} from '../assetRequestConstants';

import ADOTAdjustmentView from './adotView/ADOTAdjustmentView';
import ADOTReplenishmentView from './adotView/ADOTReplenishmentView';
import ADOTTransferView from './adotView/ADOTTransferView';
import ARDOT118TransferView from './ardotView/ARDOT118TransferView';
import SDDOTAdjustmentView from './sddotView/SDDOTAdjustmentView';
import SDDOTInventoryView from './sddotView/SDDOTInventoryView';
import SDDOTReplenishmentView from './sddotView/SDDOTReplenishmentView';
import SDDOTStockpileView from './sddotView/SDDOTStockplieView';
import ConfirmationModal from './ConfirmationModal';
import ViewRequestContext from './ViewRequestContext';
import ViewRequestHeader from './ViewRequestHeader';
import { getTransferFromAssetId } from './viewRequestUtilities';

const styles = {
  modal: {
    padding: '1.5rem',
  },
};

interface Props {
  activeRequestId: string;
  setActiveRequestId: (id: string) => void;
  changeType?: (type: AssetRequestType) => void;
  onAssetRefresh?: () => void;
  onReplenishmentSaved?: (request: AssetRequest) => void;
}

const ViewRequestModal = ({
  activeRequestId,
  setActiveRequestId,
  changeType = () => {},
  onAssetRefresh,
  onReplenishmentSaved,
}: Props) => {
  const client = useApolloClient();

  const [activeRequest, setActiveRequest] = useState<AssetRequest>();
  const [fromAsset, setFromAsset] = useState<AssetDetail>();
  const [assetLoading, setAssetLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<AssetRequestStatus>();
  const [quantityApproved, setQuantityApproved] = useState<number>();
  const [comments, setComments] = useState<string>();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);

  const handleGetFromAsset = async (id: string) => {
    if (!id) {
      return;
    }

    setAssetLoading(true);

    try {
      const { data } = await client.query({
        query: GET_ASSET,
        fetchPolicy: 'network-only',
        variables: {
          id,
        },
      });

      setFromAsset(data?.asset);
    } catch (error) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    } finally {
      setAssetLoading(false);
    }
  };

  const [getActiveRequest, { loading: activeRequestLoading }] = useLazyQuery(
    GET_ASSET_REQUEST,
    {
      fetchPolicy: 'network-only',
    },
  );

  const handleGetActiveRequest = async () => {
    try {
      const { data } = await getActiveRequest({
        variables: {
          id: activeRequestId,
        },
      });
      setActiveRequest(data.assetRequest);
      changeType(data.assetRequest.type);
      handleGetFromAsset(getTransferFromAssetId(data.assetRequest));
    } catch (error) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    }
  };

  const [updateAssetRequest] = useMutation(UPDATE_ASSET_REQUEST);

  useEffect(() => {
    if (!activeRequestId) {
      setStatus(null);
      setQuantityApproved(null);
      setComments(null);
      setActiveRequest(null);
      setFromAsset(null);
      setAssetLoading(false);
    }
  }, [activeRequestId]);

  useEffect(() => {
    if (!!activeRequestId) {
      handleGetActiveRequest();
    } else {
      setActiveRequest(null);
      setFromAsset(null);
    }
  }, [activeRequestId]);

  const isAdotTransfer =
    activeRequest?.type === AssetRequestType.ADOT_INVENTORY_TRANSFER;

  const isApprovalStatus = (requestStatus: AssetRequestStatus): boolean =>
    requestStatus === AssetRequestStatus.APPROVED ||
    (isAdotTransfer &&
      (requestStatus === AssetRequestStatus.FULL_APPROVAL ||
        requestStatus === AssetRequestStatus.PARTIAL_APPROVAL));

  const handleAdotUpdateError = (error: any) => {
    const isForbidden = getErrorCode(error) === 403;

    Snackbar.error({
      message: isForbidden
        ? ADOT_UPDATE_FORBIDDEN_MESSAGE
        : 'An error occurred. Please try again.',
    });
  };

  const resetForm = () => {
    setStatus(null);
    setQuantityApproved(null);
    setComments(null);
  };

  const updateRequest = async () => {
    setConfirmOpen(false);
    setUpdating(true);

    const requestStatus = status;

    const input = {
      id: activeRequestId,
      status: requestStatus,
      ...R.reject(R.isNil, {
        comments,
        quantityApproved,
      }),
    };

    try {
      await updateAssetRequest({ variables: { input } });
    } catch (error) {
      if (isAdotTransfer) {
        handleAdotUpdateError(error);
      } else {
        Snackbar.error({ message: 'An error occurred. Please try again.' });
      }

      return;
    } finally {
      resetForm();
      await handleGetActiveRequest();
      setUpdating(false);
    }

    if (isAdotTransfer) {
      Snackbar.info({
        message: ADOT_UPDATE_SNACKBAR_MESSAGES[requestStatus],
      });
    }

    if (
      isApprovalStatus(requestStatus) &&
      onAssetRefresh &&
      isAssetRefreshOnRequestApprovalEnabled
    ) {
      onAssetRefresh();
    }
  };

  const open = !!activeRequestId && !confirmOpen;

  const modalContent = {
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: <SDDOTInventoryView />,
    [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: <SDDOTStockpileView />,
    [AssetRequestType.SDDOT_REPLENISHMENT]: <SDDOTReplenishmentView />,
    [AssetRequestType.SDDOT_ADJUSTMENT]: <SDDOTAdjustmentView />,
    [AssetRequestType.ARDOT_118_TRANSFER]: <ARDOT118TransferView />,
    [AssetRequestType.ADOT_INVENTORY_TRANSFER]: <ADOTTransferView />,
    [AssetRequestType.ADOT_ADJUSTMENT]: <ADOTAdjustmentView />,
    [AssetRequestType.ADOT_REPLENISHMENT]: <ADOTReplenishmentView />,
  };

  const modalWidth =
    activeRequest?.type === AssetRequestType.SDDOT_REPLENISHMENT ||
    activeRequest?.type === AssetRequestType.ADOT_REPLENISHMENT
      ? 'xl'
      : 'sm';

  const loading = assetLoading || activeRequestLoading || updating;

  return (
    <ViewRequestContext.Provider
      value={{
        fromAsset,
        activeRequest,
        handleGetActiveRequest,
        setActiveRequestId,
        status,
        setStatus,
        quantityApproved,
        setQuantityApproved,
        comments,
        setComments,
        confirmOpen,
        setConfirmOpen,
        onAssetRefresh,
        onReplenishmentSaved,
      }}
    >
      <>
        <Modal
          title={<ViewRequestHeader />}
          open={open}
          onCancel={() => setActiveRequestId(null)}
          data-cy="viewAssetRequest"
          contentStyle={styles.modal}
          width={modalWidth}
          disableFooter
        >
          {loading ? <Progress /> : modalContent[activeRequest?.type]}
        </Modal>
        <ConfirmationModal updateRequest={updateRequest} />
      </>
    </ViewRequestContext.Provider>
  );
};

export default ViewRequestModal;
